import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Habit, CheckInLog } from '../types';
import { BarChart, Bar, XAxis, Tooltip, ResponsiveContainer, Cell, YAxis, CartesianGrid } from 'recharts';
import {
  format, subDays, subMonths, eachDayOfInterval, eachMonthOfInterval,
  startOfMonth, endOfMonth, getDate, startOfWeek, endOfWeek,
  addMonths, isSameMonth, isSameDay
} from 'date-fns';
import { zhCN } from 'date-fns/locale';
import { Quote, Image as ImageIcon, ChevronLeft, ChevronRight, Calendar, X } from 'lucide-react';
import { CheckInImage } from './CheckInImage';

interface StatsViewProps {
  habits: Habit[];
}

type TimeRange = 'week' | 'month' | 'year';

export const StatsView: React.FC<StatsViewProps> = ({ habits }) => {
  const [range, setRange] = useState<TimeRange>('week');
  const [calendarDate, setCalendarDate] = useState(new Date());




  // --- Calendar Logic ---
  const calendarDays = useMemo(() => {
    const start = startOfWeek(startOfMonth(calendarDate), { weekStartsOn: 1 }); // Start on Monday
    const end = endOfWeek(endOfMonth(calendarDate), { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end });
  }, [calendarDate]);

  const weekDays = ['一', '二', '三', '四', '五', '六', '日'];

  const prevMonth = () => setCalendarDate(d => subMonths(d, 1));
  const nextMonth = () => setCalendarDate(d => addMonths(d, 1));

  // --- Chart Logic ---
  const chartData = useMemo(() => {
    const today = new Date();
    let data = [];

    if (range === 'week') {
      const start = subDays(today, 6);
      const days = eachDayOfInterval({ start, end: today });
      data = days.map(day => {
        const dateStr = format(day, 'yyyy-MM-dd');
        let count = 0;
        habits.forEach(h => { if (h.completedDates.includes(dateStr)) count++; });
        return {
          label: format(day, 'EEE', { locale: zhCN }),
          count,
          fullDate: dateStr,
          tooltipLabel: format(day, 'MM月dd日')
        };
      });
    } else if (range === 'month') {
      const start = subDays(today, 29);
      const days = eachDayOfInterval({ start, end: today });
      data = days.map(day => {
        const dateStr = format(day, 'yyyy-MM-dd');
        let count = 0;
        habits.forEach(h => { if (h.completedDates.includes(dateStr)) count++; });
        return {
          label: getDate(day).toString(),
          count,
          fullDate: dateStr,
          tooltipLabel: format(day, 'MM月dd日')
        };
      });
    } else if (range === 'year') {
      const start = subMonths(today, 11);
      const months = eachMonthOfInterval({ start, end: today });
      data = months.map(month => {
        const monthKey = format(month, 'yyyy-MM');
        let count = 0;
        habits.forEach(h => {
          h.completedDates.forEach(dateStr => { if (dateStr.startsWith(monthKey)) count++; });
        });
        return {
          label: format(month, 'MMM', { locale: zhCN }),
          count,
          fullDate: monthKey,
          tooltipLabel: format(month, 'yyyy年MM月')
        };
      });
    }
    return data;
  }, [habits, range]);

  // Overall completion rate - only active (non-archived) habits
  const completionRate = useMemo(() => {
    const activeHabits = habits.filter(h => {
      const isPermanent = h.isPermanent ?? true;
      if (isPermanent) return true;
      if (h.endDate) return new Date(h.endDate) >= new Date();
      return true;
    });
    if (activeHabits.length === 0) return 0;
    const todayStr = format(new Date(), 'yyyy-MM-dd');
    const doneToday = activeHabits.filter(h => h.completedDates.includes(todayStr)).length;
    return Math.round((doneToday / activeHabits.length) * 100);
  }, [habits]);

  const totalCheckins = habits.reduce((acc, h) => acc + h.completedDates.length, 0);



  return (
    <div className="space-y-8 animate-fade-in pb-8">

      {/* 1. Monthly Ring Calendar */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700 overflow-hidden">
        <div className="p-4 flex items-center justify-between border-b border-slate-100 dark:border-slate-700">
          <div className="flex items-center space-x-2 text-slate-800 dark:text-white font-bold">
            <Calendar size={20} className="text-primary-500" />
            <span>{format(calendarDate, 'yyyy年 MM月')}</span>
          </div>
          <div className="flex space-x-1">
            <button onClick={prevMonth} className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-full transition-colors text-slate-500">
              <ChevronLeft size={20} />
            </button>
            <button onClick={nextMonth} className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-full transition-colors text-slate-500">
              <ChevronRight size={20} />
            </button>
          </div>
        </div>

        <div className="p-4">
          {/* Week Headers */}
          <div className="grid grid-cols-7 mb-2">
            {weekDays.map(d => (
              <div key={d} className="text-center text-xs text-slate-400 font-medium py-1">{d}</div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-y-2 gap-x-1">
            {calendarDays.map((day, dayIdx) => {
              const dateStr = format(day, 'yyyy-MM-dd');
              const isCurrentMonth = isSameMonth(day, calendarDate);
              const isToday = isSameDay(day, new Date());


              // Find completed habits for this day (exclude archived)
              const completedHabitsForDay = habits
                .filter(h => {
                  const isPermanent = h.isPermanent ?? true;
                  if (isPermanent) return true;
                  if (h.endDate) return new Date(h.endDate) >= new Date();
                  return true;
                })
                .filter(h => h.completedDates.includes(dateStr))
                .sort((a, b) => a.id.localeCompare(b.id));

              return (
                <div
                  key={dateStr}
                  className={`
                     relative aspect-square flex items-center justify-center rounded-xl transition-all hover:scale-105
                     ${!isCurrentMonth ? 'opacity-30' : ''} 
                     ${isToday ? 'bg-slate-50 dark:bg-slate-700/50' : ''}
                     hover:bg-slate-50 dark:hover:bg-slate-700/30
                   `}
                >
                  <span className={`text-xs z-10 font-medium ${isToday ? 'text-primary-600 dark:text-primary-400 font-bold' : 'text-slate-600 dark:text-slate-400'}`}>
                    {format(day, 'd')}
                  </span>

                  {/* Concentric Rings */}
                  <div className="absolute inset-0 pointer-events-none">
                    <svg viewBox="0 0 36 36" className="w-full h-full rotate-[-90deg]">
                      {completedHabitsForDay.map((h, i) => {
                        const r = 17 - (i * 2.5);
                        if (r <= 0) return null;

                        const colorClass = h.color.replace('bg-', 'text-');

                        return (
                          <circle
                            key={h.id}
                            cx="18"
                            cy="18"
                            r={r}
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            className={`${colorClass} opacity-80`}
                          />
                        );
                      })}
                    </svg>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Legend - Only active (non-archived) habits */}
          <div className="mt-6 flex flex-wrap gap-3 justify-center border-t border-slate-50 dark:border-slate-800 pt-4">
            {habits.filter(h => {
              const isPermanent = h.isPermanent ?? true;
              if (isPermanent) return true;
              if (h.endDate) return new Date(h.endDate) >= new Date();
              return true;
            }).map(h => (
              <div key={h.id} className="flex items-center space-x-1.5">
                <div className={`w-3 h-3 rounded-full ${h.color} ring-2 ring-white dark:ring-slate-800`}></div>
                <span className="text-xs text-slate-600 dark:text-slate-400">{h.title}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 2. Charts Section */}
      <div className="space-y-6">
        <div className="flex justify-between items-center px-2">
          <h2 className="text-xl font-bold text-slate-800 dark:text-white">统计趋势</h2>
          <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-lg">
            {(['week', 'month', 'year'] as TimeRange[]).map((r) => (
              <button
                key={r}
                onClick={() => setRange(r)}
                className={`
                  px-3 py-1 text-xs font-medium rounded-md transition-all
                  ${range === r
                    ? 'bg-white dark:bg-slate-700 text-slate-800 dark:text-white shadow-sm'
                    : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'}
                `}
              >
                {r === 'week' ? '本周' : r === 'month' ? '本月' : '全年'}
              </button>
            ))}
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-4 rounded-2xl text-white shadow-lg relative overflow-hidden">
            <div className="absolute top-0 right-0 -mr-4 -mt-4 w-24 h-24 bg-white opacity-10 rounded-full blur-2xl"></div>
            <p className="text-blue-100 text-sm font-medium">今日完成率</p>
            <div className="text-3xl font-bold mt-2">{completionRate}%</div>
          </div>
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-4 rounded-2xl text-white shadow-lg relative overflow-hidden">
            <div className="absolute top-0 right-0 -mr-4 -mt-4 w-24 h-24 bg-white opacity-10 rounded-full blur-2xl"></div>
            <p className="text-purple-100 text-sm font-medium">累计打卡</p>
            <div className="text-3xl font-bold mt-2">{totalCheckins} <span className="text-sm font-normal opacity-80">次</span></div>
          </div>
        </div>

        {/* Bar Chart */}
        <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700">
          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 5, right: 0, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" opacity={0.5} />
                <XAxis
                  dataKey="label"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#94a3b8', fontSize: 10 }}
                  dy={10}
                  interval={range === 'month' ? 2 : 0}
                />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 10 }} />
                <Tooltip
                  cursor={{ fill: 'rgba(0,0,0,0.03)' }}
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      return (
                        <div className="bg-slate-800 text-white text-xs p-2 rounded-lg shadow-xl">
                          <p className="font-bold mb-1">{data.tooltipLabel}</p>
                          <p>打卡: {data.count} 次</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Bar dataKey="count" radius={[4, 4, 0, 0]} maxBarSize={40}>
                  {chartData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.fullDate === format(new Date(), 'yyyy-MM-dd') || entry.fullDate === format(new Date(), 'yyyy-MM')
                        ? '#0ea5e9' // Sky-500
                        : '#cbd5e1'}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>

  );
};